import type {
  AccessSettingsPublic,
  ApplicationTagPublic,
  BrandingPublic,
  OperationalSettingsPublic,
  TotpRequirement,
} from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useRef, useState } from 'react';

import { STR } from '../../i18n';
import {
  ApiError,
  createApplicationTag,
  getAccessSettings,
  getOperationalSettings,
  listApplicationTags,
  removeBrandingLogo,
  restoreApplicationTag,
  retireApplicationTag,
  updateAccessSettings,
  updateApplicationTag,
  updateBranding,
  updateOperationalSettings,
  uploadBrandingLogo,
} from '../../lib/api';
import { brandGlyph, useBranding } from '../../lib/branding';
import { queryKeys } from '../../lib/queryKeys';
import { type SurfaceId, surfaceById } from '../../lib/surfaces';
import { showToast } from '../../lib/toast';
import { SlotBrandMark } from '../../ui/BrandMark';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../ui/Modal';
import {
  Callout,
  Card,
  CardHeader,
  Chip,
  EmptyRow,
  MasterDetailSkeleton,
  Skeleton,
} from '../../ui/Primitives';
import { SectionMenu, type SectionMenuEntry } from '../../ui/SectionMenu';
import {
  buttonStyle,
  checkboxStyle,
  eyebrowStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  primaryButtonStyle,
  quietButtonStyle,
  rowActionStyle,
  textFieldStyle,
} from '../../ui/styles';

import { AdminShell } from './AdminShell';
import {
  accessSavedSignature,
  exportWithoutSee,
  SWITCH_KEYS,
  SWITCH_ROLES,
  type SwitchKey,
  type SwitchRole,
  switchesEqual,
  withSwitch,
} from './settings/accessSwitches';
import { EmailSettingsSection } from './settings/EmailSettingsSection';

const INTERVAL_MIN = 15;
const INTERVAL_MAX = 3600;
const THRESHOLD_MIN = 1;
const THRESHOLD_MAX = 10;
const SESSION_TTL_MIN = 1;
const SESSION_TTL_MAX = 365;
const INTROSPECTION_MIN = 30;
const INTROSPECTION_MAX = 3600;
const AUDIT_PAGE_MIN = 10;
const AUDIT_PAGE_MAX = 200;
const CLAMP_LINES_MIN = 1;
const CLAMP_LINES_MAX = 8;
const SESSION_PURGE_MIN = 1;
const SESSION_PURGE_MAX = 365;
/**
 * The server's own ceiling is 2, DELIBERATELY (see the shared contract): each step widens
 * the window in which any one code verifies, so beyond ±2 this stops being drift tolerance
 * and becomes a replay window. These bounds are affordance; the server refuses independently.
 */
const TOTP_DRIFT_MIN = 0;
const TOTP_DRIFT_MAX = 2;
/** The server's own bounds for the app-deactivation overdue threshold (1..168 hours). */
const OVERDUE_HOURS_MIN = 1;
const OVERDUE_HOURS_MAX = 168;

/**
 * U5 — the six rate-limit bounds, mirrored from the server's own constants
 * (server/src/config/settings.ts) as INPUT AFFORDANCE and nothing more. Unlike the
 * Operations card above, the Rate limits card runs no submit-time check against them:
 * the server's bounds are the real ones, and its refusal is what renders. A number
 * outside these is typed, sent, and answered by the server in its own words.
 */
const RATE_LIMIT_COUNT_MIN = 1;
const LOGIN_RATE_LIMIT_MAX_BOUND = 10000;
const INTROSPECT_RATE_LIMIT_MAX_BOUND = 100000;
const INTROSPECT_FAILURE_RATE_LIMIT_MAX_BOUND = 10000;
const RATE_LIMIT_WINDOW_MIN = 1;
const RATE_LIMIT_WINDOW_MAX = 86400;

/** The three states the server accepts — rendered as a select, defaulting to 'optional'. */
const TOTP_REQUIREMENT_VALUES: readonly TotpRequirement[] = [
  'optional',
  'required_admins',
  'required_all',
];

interface Draft {
  name: string;
  tagline: string;
  taglineSub: string;
  glyph: string;
}

const ACCEPTED_LOGO_TYPES = 'image/png,image/jpeg,image/webp';

function draftFrom(branding: BrandingPublic): Draft {
  return {
    name: branding.name,
    tagline: branding.tagline,
    taglineSub: branding.taglineSub,
    glyph: branding.glyph,
  };
}

/** Changes when a save lands, which is exactly when the editor should re-seed its draft. */
function savedSignature(branding: BrandingPublic): string {
  return [branding.name, branding.tagline, branding.taglineSub, branding.glyph].join(' ');
}

interface OperationalDraft {
  intervalSeconds: string;
  failureThreshold: string;
  sessionTtlDays: string;
  introspectionSeconds: string;
  auditPageSize: string;
  /** How many lines of an announcement body the launcher shows before "View more". */
  announcementClampLines: string;
  sessionPurgeDays: string;
  totpDriftSteps: string;
  /** A closed enum from a select — never free text, so it needs no submit-time parse. */
  totpRequirement: TotpRequirement;
}

function operationalDraftFrom(settings: OperationalSettingsPublic): OperationalDraft {
  return {
    intervalSeconds: String(settings.healthPollIntervalSeconds),
    failureThreshold: String(settings.healthPollFailureThreshold),
    sessionTtlDays: String(settings.sessionTtlDays),
    introspectionSeconds: String(settings.introspectionCacheTtlSeconds),
    auditPageSize: String(settings.auditPageSize),
    announcementClampLines: String(settings.announcementClampLines),
    sessionPurgeDays: String(settings.sessionPurgeDays),
    totpDriftSteps: String(settings.totpDriftSteps),
    totpRequirement: settings.totpRequirement,
  };
}

/**
 * Same rule as `savedSignature` above — a landed save re-seeds this editor's own draft.
 *
 * DELIBERATELY ONLY THIS CARD'S OWN NINE FIELDS, not the whole settings object. Two cards
 * now write the one operational PUT (U5), and a signature spanning all fifteen would mean
 * saving one card remounts the other and throws away an in-progress draft it never touched.
 */
function operationalSavedSignature(settings: OperationalSettingsPublic): string {
  return [
    settings.healthPollIntervalSeconds,
    settings.healthPollFailureThreshold,
    settings.sessionTtlDays,
    settings.introspectionCacheTtlSeconds,
    settings.auditPageSize,
    settings.announcementClampLines,
    settings.sessionPurgeDays,
    settings.totpDriftSteps,
    settings.totpRequirement,
  ]
    .map(String)
    .join(' ');
}

/** U5 — the six rate-limit fields as a string, for the same remount-on-save reason. */
interface RateLimitDraft {
  loginMax: string;
  loginWindow: string;
  introspectMax: string;
  introspectWindow: string;
  introspectFailureMax: string;
  introspectFailureWindow: string;
}

function rateLimitDraftFrom(settings: OperationalSettingsPublic): RateLimitDraft {
  return {
    loginMax: String(settings.loginRateLimitMax),
    loginWindow: String(settings.loginRateLimitWindowSeconds),
    introspectMax: String(settings.introspectRateLimitMax),
    introspectWindow: String(settings.introspectRateLimitWindowSeconds),
    introspectFailureMax: String(settings.introspectFailureRateLimitMax),
    introspectFailureWindow: String(settings.introspectFailureRateLimitWindowSeconds),
  };
}

function rateLimitSavedSignature(settings: OperationalSettingsPublic): string {
  return [
    settings.loginRateLimitMax,
    settings.loginRateLimitWindowSeconds,
    settings.introspectRateLimitMax,
    settings.introspectRateLimitWindowSeconds,
    settings.introspectFailureRateLimitMax,
    settings.introspectFailureRateLimitWindowSeconds,
  ]
    .map(String)
    .join(' ');
}

export type SettingsSection = 'general' | 'operations' | 'email' | 'access';

const SECTIONS: readonly { id: SettingsSection; surface: SurfaceId; label: string }[] = [
  { id: 'general', surface: 'settings', label: STR.settings.menu.general },
  { id: 'operations', surface: 'settingsOperations', label: STR.settings.menu.operations },
  { id: 'email', surface: 'settingsEmail', label: STR.settings.menu.email },
  { id: 'access', surface: 'settingsAccess', label: STR.settings.menu.access },
];

/**
 * R6 — SETTINGS IS A REAL SURFACE, and since SIR-7804 a hub like Access: the same
 * `SectionMenu`, one route per section, each card with its own Save. General holds the
 * Launcher tag vocabulary and branding; Operations the Operations card (health polling,
 * session lifetime, introspection cache, audit page size, announcement lines, session
 * purge, two-step) and the Rate limits card (U5); Email & notifications the mail setup and
 * per-event switches (`settings/EmailSettingsSection.tsx`); Access the access module's own
 * settings. The existing cards moved between sections unchanged.
 *
 * WHAT IS NO LONGER HERE: the announcement-colour card. An application's colour is a column
 * on the application row now and is edited on its own detail pane's Launcher tile card (U2)
 * — one application, one place. A TYPE's colour was never a settings fact and keeps its own
 * home on Announcements → Types. The tag vocabulary stays: it has no other home.
 */
export function SettingsPage({ section }: { section: SettingsSection }) {
  const current = SECTIONS.find((entry) => entry.id === section);
  const entries: SectionMenuEntry[] = SECTIONS.map((entry) => ({
    id: entry.id,
    path: surfaceById(entry.surface).path,
    label: entry.label,
  }));

  let body: ReactNode = null;
  switch (section) {
    case 'general':
      body = <GeneralSection />;
      break;
    case 'operations':
      body = <OperationsSection />;
      break;
    case 'email':
      body = <EmailSettingsSection />;
      break;
    case 'access':
      body = <AccessSection />;
      break;
  }

  return (
    <AdminShell surfaceId={current?.surface ?? 'settings'}>
      <SectionMenu label={STR.settings.menu.label} entries={entries} current={section}>
        {body}
      </SectionMenu>
    </AdminShell>
  );
}

/**
 * One section's column. No max-width, deliberately (D23): the content fills the pane at
 * every width. One full-width column — `settings-cols` and its 360px preview track are
 * retired (Wave 3B fix round), because any reserved preview column is a max-width by
 * another name.
 */
function SectionColumn({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: '32px 40px 110px', minWidth: 0 }}>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {children}
      </div>
    </div>
  );
}

function GeneralSection() {
  const brandingQuery = useBranding();

  // The preview disclosure's open state lives HERE, not in the editor: the editor remounts
  // on every landed save (it is keyed on the saved values below), and a save must not slam
  // an open preview shut mid-session. Plain component state on purpose — no localStorage,
  // no query param, no context — so every fresh visit mounts collapsed, by rule.
  const [previewOpen, setPreviewOpen] = useState(false);

  const saved = brandingQuery.data;
  if (saved === undefined) return <MasterDetailSkeleton />;

  return (
    <SectionColumn>
      {/* The managed launcher tag vocabulary. Kept in Settings deliberately: unlike an
          application's colour (which moved to the application, U2), a tag belongs to no
          single application and has no other home. */}
      <LauncherTagsCard />

      {/* Branding sits last, deliberately: it is set once. Keyed on the saved values: a
          successful save remounts the editor with a clean draft, which is how the seed
          happens without a setState-inside-an-effect. */}
      <BrandingEditor
        key={savedSignature(saved)}
        saved={saved}
        previewOpen={previewOpen}
        onTogglePreview={() => {
          setPreviewOpen((open) => !open);
        }}
      />
    </SectionColumn>
  );
}

function OperationsSection() {
  const operationalQuery = useQuery({
    queryKey: queryKeys.operationalSettings,
    queryFn: getOperationalSettings,
  });

  const saved = operationalQuery.data?.settings;
  if (saved === undefined) return <MasterDetailSkeleton />;

  return (
    <SectionColumn>
      {/* Ruling 1 — keyed on the saved values, so a successful save re-seeds its own draft.
          The editor itself stays layout-ignorant: a plain full-width sibling in the column. */}
      <OperationalSettingsEditor key={operationalSavedSignature(saved)} saved={saved} />

      {/* U5 — the rate-limit card. Same anatomy and same key-on-saved rule as the card
          above; it writes the SAME operational PUT, so it is handed the whole saved object
          and re-sends the fields it does not own. */}
      <RateLimitsEditor key={rateLimitSavedSignature(saved)} saved={saved} />
    </SectionColumn>
  );
}

function AccessSection() {
  const accessQuery = useQuery({ queryKey: queryKeys.accessSettings, queryFn: getAccessSettings });

  const saved = accessQuery.data?.settings;
  if (saved === undefined) return <MasterDetailSkeleton />;

  return (
    <SectionColumn>
      <AccessSettingsEditor key={accessSavedSignature(saved)} saved={saved} />
    </SectionColumn>
  );
}

/**
 * The text fields are a dirty-tracked form saved as ONE write. The logo is a separate pair
 * of endpoints (upload / remove) and therefore applies immediately — pretending otherwise
 * would mean holding an uploaded file in the browser and lying about when it took effect.
 *
 * The preview is a collapsed-by-default disclosure INSIDE the Branding card (Wave 3B fix
 * round — `settings-cols` and its 360px preview column are retired), placed after the last
 * branding field and before the Discard/Save footer. `previewOpen`/`onTogglePreview` come
 * from the parent so the state survives this editor's save-keyed remounts; see the note
 * there for why nothing persists it beyond component state.
 */
function BrandingEditor({
  saved,
  previewOpen,
  onTogglePreview,
}: {
  saved: BrandingPublic;
  previewOpen: boolean;
  onTogglePreview: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(saved));
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.branding });
  }

  const saveMutation = useMutation({
    mutationFn: updateBranding,
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.brandingSaved);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: uploadBrandingLogo,
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.logoUpdated);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const removeLogoMutation = useMutation({
    mutationFn: removeBrandingLogo,
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.logoRemoved, 'warn');
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const dirty =
    draft.name !== saved.name ||
    draft.tagline !== saved.tagline ||
    draft.taglineSub !== saved.taglineSub ||
    draft.glyph !== saved.glyph;

  const previewGlyph = brandGlyph({ glyph: draft.glyph, name: draft.name });
  const previewName = draft.name.trim();
  const previewTagline = draft.tagline.trim();
  const previewSub = draft.taglineSub.trim();

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
    setError(null);
  }

  function save() {
    const name = draft.name.trim();
    if (name === '') {
      setError(STR.settings.nameRequired);
      return;
    }
    // The server requires 1–2 characters for the glyph; an empty field means "derive it",
    // which is exactly what the sign-in panel does, so send the derived letter.
    const glyph = draft.glyph.trim() === '' ? brandGlyph({ glyph: '', name }) : draft.glyph.trim();
    saveMutation.mutate({
      name,
      tagline: draft.tagline.trim(),
      taglineSub: draft.taglineSub.trim(),
      glyph,
    });
  }

  return (
    <Card style={{ minWidth: 0 }}>
      <CardHeader title={STR.settings.brandingTitle} />
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}

        <div>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.logoLabel}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <SlotBrandMark logoUrl={saved.logoUrl} glyph={brandGlyph(saved)} />
            <button
              type="button"
              className="ghost"
              style={buttonStyle}
              disabled={uploadMutation.isPending}
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              {STR.settings.chooseLogo}
            </button>
            {saved.logoUrl === null ? null : (
              <button
                type="button"
                className="tbtn tbtn-danger"
                style={rowActionStyle}
                disabled={removeLogoMutation.isPending}
                onClick={() => {
                  removeLogoMutation.mutate();
                }}
              >
                {STR.settings.removeLogo}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_LOGO_TYPES}
              style={{ display: 'none' }}
              aria-hidden="true"
              tabIndex={-1}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file !== undefined) uploadMutation.mutate(file);
              }}
            />
          </div>
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.logoHint}
          </span>
        </div>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.nameLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="text"
            maxLength={60}
            value={draft.name}
            onChange={(event) => {
              set('name', event.target.value);
            }}
            style={textFieldStyle}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.nameHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span
            className="tx-muted"
            style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
          >
            {STR.settings.taglineLabel}{' '}
            <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
              {STR.settings.optional}
            </span>
          </span>
          <input
            className="field"
            type="text"
            maxLength={120}
            value={draft.tagline}
            onChange={(event) => {
              set('tagline', event.target.value);
            }}
            style={textFieldStyle}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.taglineHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span
            className="tx-muted"
            style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
          >
            {STR.settings.subLabel}{' '}
            <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
              {STR.settings.optional}
            </span>
          </span>
          <input
            className="field"
            type="text"
            maxLength={160}
            value={draft.taglineSub}
            onChange={(event) => {
              set('taglineSub', event.target.value);
            }}
            style={textFieldStyle}
          />
        </label>

        <label style={{ display: 'block' }}>
          <span
            className="tx-muted"
            style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
          >
            {STR.settings.glyphLabel}{' '}
            <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
              {STR.settings.optional}
            </span>
          </span>
          <input
            className="field"
            type="text"
            maxLength={2}
            value={draft.glyph}
            onChange={(event) => {
              set('glyph', event.target.value);
            }}
            style={{ ...textFieldStyle, width: 72, textAlign: 'center', fontWeight: 650 }}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.glyphHint}
          </span>
        </label>
      </div>

      {/* The preview disclosure — the Grants screen's "Show revoked history" row is the
          pattern precedent: a full-width surface-2 row holding a real button with
          aria-expanded, the content rendered beneath it when open. */}
      <div
        style={{
          padding: '10px 22px',
          background: 'var(--surface-2)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          className="tbtn"
          aria-expanded={previewOpen}
          onClick={onTogglePreview}
          style={quietButtonStyle}
        >
          {previewOpen ? STR.settings.hidePreview : STR.settings.showPreview}
        </button>
      </div>

      {previewOpen ? (
        <div
          style={{
            padding: '18px 22px',
            background: 'var(--surface-2)',
            borderTop: '1px solid var(--border)',
          }}
        >
          {/* The frame is the ROW: the mock takes ~70% of it, left-aligned with the
              fields above (less the frame's own 15px inset), and the caption fills the
              space beside it — wrapping beneath when fewer than ~180px remain. */}
          <div
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 9,
              padding: 14,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 14,
            }}
          >
            <div
              style={{
                position: 'relative',
                overflow: 'hidden',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 9,
                // Was a fixed `height: 220` — a ceiling with nowhere for real content to
                // go, so the seeded default tagline+sub clipped by 51px at every width
                // measured (1440 down to 900): not a responsive edge case, a box that
                // could never fit its own default text. `minHeight` keeps the box's visual
                // weight for short content (what 220 was drawn for) while letting it grow
                // for longer, still-legitimate branding text instead of losing it.
                // `overflow: hidden` stays — it is also what correctly crops the decorative
                // glyph bleeding past the right/bottom edge (`right: -14`, `bottom: -38`
                // below), the same intentional treatment `.b-glyph` / `.c-glyph` use on the
                // real sign-in and launcher pages.
                minHeight: 220,
                // OPERATOR-RULED SCALING (Wave 3B fix round). The mock GROWS to ~70% of
                // the row — a fixed-width mock left 71–81% of the row as dead margin at
                // every measured width — and its type scales with it via container-query
                // units (this element is the container; 1cqw = 1% of its content width,
                // 282px at the mock's original 284px size, which is where each clamp's
                // minimum reproduces the original type exactly). Each size CLAMPS so it
                // never passes ~25% over what the real sign-in page renders (.b-brand in
                // components.css): tagline 19px -> 23.5, sub 12.5 -> 15.5, name 14 ->
                // 17.5, text max-width 300 -> 375, inner padding capped at the real
                // page's own 36/40. The glyph caps at the real page's 300px, deliberately
                // under its +25% allowance: minHeight 220 pins the box height, and a
                // 375px glyph in a ~220px box crops the top of the letter — a crop the
                // real page never shows. A cap on a MOCK, not on the column: D23 holds.
                flex: '0 0 70%',
                maxWidth: '100%',
                containerType: 'inline-size',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 'clamp(18px, 6.4cqw, 36px) clamp(20px, 7.1cqw, 40px)',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <SlotBrandMark logoUrl={saved.logoUrl} glyph={previewGlyph} />
                  <span
                    style={{
                      fontSize: 'clamp(14px, 5cqw, 17.5px)',
                      fontWeight: 700,
                      letterSpacing: '-.01em',
                    }}
                  >
                    {previewName}
                  </span>
                </div>
                {previewTagline === '' ? null : (
                  <p
                    style={{
                      margin: 'auto 0 0',
                      fontSize: 'clamp(15px, 5.3cqw, 23.5px)',
                      fontWeight: 650,
                      letterSpacing: '-.015em',
                      lineHeight: 1.4,
                      // The old fixed 230px cap was the actual clipped-tagline cause:
                      // the seeded tagline measured flush against it with zero slack.
                      // It scales with the card now, so the text always has room.
                      maxWidth: 'clamp(230px, 81.5cqw, 375px)',
                      textWrap: 'pretty',
                      position: 'relative',
                    }}
                  >
                    {previewTagline}
                  </p>
                )}
                {previewSub === '' ? null : (
                  <p
                    style={{
                      margin: '7px 0 0',
                      fontSize: 'clamp(11px, 3.9cqw, 15.5px)',
                      color: 'var(--subtle)',
                      lineHeight: 1.55,
                      maxWidth: 'clamp(230px, 81.5cqw, 375px)',
                      textWrap: 'pretty',
                      position: 'relative',
                    }}
                  >
                    {previewSub}
                  </p>
                )}
              </div>
              <div
                style={{
                  position: 'absolute',
                  right: -14,
                  bottom: -38,
                  fontSize: 'clamp(150px, 53cqw, 300px)',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'var(--faint)',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                aria-hidden="true"
              >
                {previewGlyph}
              </div>
            </div>
            <span
              className="tx-subtle"
              style={{ ...hintStyle, marginTop: 0, flex: '1 1 180px' }}
            >
              {STR.settings.previewNote}
            </span>
          </div>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '12px 22px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 11px 11px',
        }}
      >
        <button
          type="button"
          className="tbtn"
          style={quietButtonStyle}
          disabled={!dirty}
          onClick={() => {
            setDraft(draftFrom(saved));
            setError(null);
            showToast(STR.toast.brandingDiscarded, 'warn');
          }}
        >
          {STR.settings.discardChanges}
        </button>
        {dirty ? (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Chip tone="attention">{STR.settings.unsaved}</Chip>
            <button
              type="button"
              className="prim"
              style={primaryButtonStyle}
              disabled={saveMutation.isPending}
              onClick={save}
            >
              {saveMutation.isPending ? STR.settings.saving : STR.settings.save}
            </button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * RULING 1 — mirrors `BrandingEditor` above exactly: a dirty-tracked form saved as one
 * write, the same field/label/hint treatment, the same discard/unsaved-chip/save footer,
 * the same primitives. The one thing this form adds beyond branding's own fields: the
 * bounds (15–3600s, 1–10 failures, 1–365 days, 30–3600s, 10–200 rows, 1–8 lines) are
 * client-side AFFORDANCE only,
 * checked at submit time exactly the way branding checks `name` is non-empty — never by
 * disabling Save, so a genuine server refusal (its bounds are the real ones) always has a
 * path to render.
 *
 * Wave 3B adds session lifetime and the introspection cache to this same card (operator-
 * ruled), which is why it is now titled "Operations" rather than "Health polling". The
 * introspection hint carries the D24 sentence: that number IS the suite's deprovisioning
 * window, and the help text must say so plainly.
 */
function OperationalSettingsEditor({ saved }: { saved: OperationalSettingsPublic }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<OperationalDraft>(() => operationalDraftFrom(saved));
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.operationalSettings });
  }

  const saveMutation = useMutation({
    mutationFn: updateOperationalSettings,
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.operationalSettingsSaved);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const dirty =
    draft.intervalSeconds !== String(saved.healthPollIntervalSeconds) ||
    draft.failureThreshold !== String(saved.healthPollFailureThreshold) ||
    draft.sessionTtlDays !== String(saved.sessionTtlDays) ||
    draft.introspectionSeconds !== String(saved.introspectionCacheTtlSeconds) ||
    draft.auditPageSize !== String(saved.auditPageSize) ||
    draft.announcementClampLines !== String(saved.announcementClampLines) ||
    draft.sessionPurgeDays !== String(saved.sessionPurgeDays) ||
    draft.totpDriftSteps !== String(saved.totpDriftSteps) ||
    draft.totpRequirement !== saved.totpRequirement;

  function set<K extends keyof OperationalDraft>(key: K, value: OperationalDraft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
    setError(null);
  }

  function save() {
    const interval = Number(draft.intervalSeconds);
    if (!Number.isInteger(interval) || interval < INTERVAL_MIN || interval > INTERVAL_MAX) {
      setError(STR.settings.intervalInvalid);
      return;
    }
    const threshold = Number(draft.failureThreshold);
    if (!Number.isInteger(threshold) || threshold < THRESHOLD_MIN || threshold > THRESHOLD_MAX) {
      setError(STR.settings.thresholdInvalid);
      return;
    }
    const sessionTtl = Number(draft.sessionTtlDays);
    if (
      !Number.isInteger(sessionTtl) ||
      sessionTtl < SESSION_TTL_MIN ||
      sessionTtl > SESSION_TTL_MAX
    ) {
      setError(STR.settings.sessionTtlInvalid);
      return;
    }
    const introspection = Number(draft.introspectionSeconds);
    if (
      !Number.isInteger(introspection) ||
      introspection < INTROSPECTION_MIN ||
      introspection > INTROSPECTION_MAX
    ) {
      setError(STR.settings.introspectionInvalid);
      return;
    }
    const auditPage = Number(draft.auditPageSize);
    if (!Number.isInteger(auditPage) || auditPage < AUDIT_PAGE_MIN || auditPage > AUDIT_PAGE_MAX) {
      setError(STR.settings.auditPageSizeInvalid);
      return;
    }
    const clampLines = Number(draft.announcementClampLines);
    if (
      !Number.isInteger(clampLines) ||
      clampLines < CLAMP_LINES_MIN ||
      clampLines > CLAMP_LINES_MAX
    ) {
      setError(STR.settings.announcementClampLinesInvalid);
      return;
    }
    const sessionPurge = Number(draft.sessionPurgeDays);
    if (
      !Number.isInteger(sessionPurge) ||
      sessionPurge < SESSION_PURGE_MIN ||
      sessionPurge > SESSION_PURGE_MAX
    ) {
      setError(STR.settings.sessionPurgeDaysInvalid);
      return;
    }
    const drift = Number(draft.totpDriftSteps);
    if (!Number.isInteger(drift) || drift < TOTP_DRIFT_MIN || drift > TOTP_DRIFT_MAX) {
      setError(STR.settings.totpDriftInvalid);
      return;
    }
    /*
     * THE SECTION SAVES AS A UNIT — every field is required in the body, so the six
     * rate-limit fields this card does not own travel at their SAVED values. That is not
     * padding: re-sending what is stored is exactly what "unchanged" means on a PUT whose
     * absence would otherwise read as a request to change them. The Rate limits card does
     * the mirror image with these nine.
     */
    saveMutation.mutate({
      healthPollIntervalSeconds: interval,
      healthPollFailureThreshold: threshold,
      sessionTtlDays: sessionTtl,
      introspectionCacheTtlSeconds: introspection,
      auditPageSize: auditPage,
      sessionPurgeDays: sessionPurge,
      totpDriftSteps: drift,
      totpRequirement: draft.totpRequirement,
      announcementClampLines: clampLines,
      loginRateLimitMax: saved.loginRateLimitMax,
      loginRateLimitWindowSeconds: saved.loginRateLimitWindowSeconds,
      introspectRateLimitMax: saved.introspectRateLimitMax,
      introspectRateLimitWindowSeconds: saved.introspectRateLimitWindowSeconds,
      introspectFailureRateLimitMax: saved.introspectFailureRateLimitMax,
      introspectFailureRateLimitWindowSeconds: saved.introspectFailureRateLimitWindowSeconds,
    });
  }

  return (
    <Card>
      <CardHeader title={STR.settings.operationalTitle} />
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.intervalLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={INTERVAL_MIN}
            max={INTERVAL_MAX}
            step={1}
            value={draft.intervalSeconds}
            onChange={(event) => {
              set('intervalSeconds', event.target.value);
            }}
            style={textFieldStyle}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.intervalHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.thresholdLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={THRESHOLD_MIN}
            max={THRESHOLD_MAX}
            step={1}
            value={draft.failureThreshold}
            onChange={(event) => {
              set('failureThreshold', event.target.value);
            }}
            style={textFieldStyle}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.thresholdHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.sessionTtlLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={SESSION_TTL_MIN}
            max={SESSION_TTL_MAX}
            step={1}
            value={draft.sessionTtlDays}
            onChange={(event) => {
              set('sessionTtlDays', event.target.value);
            }}
            style={textFieldStyle}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.sessionTtlHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.introspectionLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={INTROSPECTION_MIN}
            max={INTROSPECTION_MAX}
            step={1}
            value={draft.introspectionSeconds}
            onChange={(event) => {
              set('introspectionSeconds', event.target.value);
            }}
            style={textFieldStyle}
          />
          {/* D24 — the deprovisioning window, stated plainly. See the note in i18n.ts. */}
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.introspectionHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.auditPageSizeLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={AUDIT_PAGE_MIN}
            max={AUDIT_PAGE_MAX}
            step={1}
            value={draft.auditPageSize}
            onChange={(event) => {
              set('auditPageSize', event.target.value);
            }}
            style={textFieldStyle}
          />
          {/* A screen preference — the hint says so; the CLI and API defaults stand apart. */}
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.auditPageSizeHint}
          </span>
        </label>

        {/* Beside the audit page size deliberately: the two screen-presentation preferences
            in this card sit together, above the lifecycle and two-step fields. */}
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.announcementClampLinesLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={CLAMP_LINES_MIN}
            max={CLAMP_LINES_MAX}
            step={1}
            value={draft.announcementClampLines}
            onChange={(event) => {
              set('announcementClampLines', event.target.value);
            }}
            style={textFieldStyle}
          />
          {/* Presentation only, and the hint says what it governs and where — the full
              announcement is always one click away, whatever this is set to. */}
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.announcementClampLinesHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.sessionPurgeDaysLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={SESSION_PURGE_MIN}
            max={SESSION_PURGE_MAX}
            step={1}
            value={draft.sessionPurgeDays}
            onChange={(event) => {
              set('sessionPurgeDays', event.target.value);
            }}
            style={textFieldStyle}
          />
          {/* The non-obvious part, stated plainly: this is also the per-device sign-out
              reach window — see the hint's own wording in i18n.ts. */}
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.sessionPurgeDaysHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.totpDriftLabel}
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={TOTP_DRIFT_MIN}
            max={TOTP_DRIFT_MAX}
            step={1}
            value={draft.totpDriftSteps}
            onChange={(event) => {
              set('totpDriftSteps', event.target.value);
            }}
            style={textFieldStyle}
          />
          {/* OPERATOR-RULED WORDING — drift tolerance in plain time, never TOTP jargon. */}
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.totpDriftHint}
          </span>
        </label>

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.totpRequirementLabel}
          </span>
          <select
            className="field"
            value={draft.totpRequirement}
            onChange={(event) => {
              set('totpRequirement', event.target.value as TotpRequirement);
            }}
            style={{ ...textFieldStyle, padding: '9px 30px 9px 11px' }}
          >
            {TOTP_REQUIREMENT_VALUES.map((value) => (
              <option key={value} value={value}>
                {STR.settings.totpRequirementOptions[value]}
              </option>
            ))}
          </select>
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.totpRequirementHint}
          </span>
        </label>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '12px 22px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 11px 11px',
        }}
      >
        <button
          type="button"
          className="tbtn"
          style={quietButtonStyle}
          disabled={!dirty}
          onClick={() => {
            setDraft(operationalDraftFrom(saved));
            setError(null);
            showToast(STR.toast.operationalSettingsDiscarded, 'warn');
          }}
        >
          {STR.settings.discardChanges}
        </button>
        {dirty ? (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Chip tone="attention">{STR.settings.unsaved}</Chip>
            <button
              type="button"
              className="prim"
              style={primaryButtonStyle}
              disabled={saveMutation.isPending}
              onClick={save}
            >
              {saveMutation.isPending ? STR.settings.saving : STR.settings.save}
            </button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * U5 — THE RATE LIMIT CARD. The R6 placeholder that stood here is gone; these six settings
 * are real, live-read, and now reachable without a psql session.
 *
 * ANATOMY IS THE OPERATIONS CARD'S, deliberately: the same field/label/hint treatment, the
 * same dirty tracking, the same Discard / unsaved-chip / Save footer, the same primitives,
 * and the same key-on-saved remount that re-seeds a landed save's draft.
 *
 * IT WRITES THE SAME PUT, and that PUT takes the whole operational section as a unit — so
 * this card is handed the entire saved object and re-sends the nine fields it does not own
 * unchanged, exactly as the Operations card re-sends these six. Each card's remount key
 * covers ONLY its own fields, so saving one never discards a draft in the other.
 *
 * NO CLIENT-SIDE BOUNDS CHECK, by ruling and unlike the card above. The server's bounds are
 * the real ones; this card sends what was typed and renders the server's refusal. `min`/`max`
 * on the inputs are stepper affordance and refuse nothing. A field left empty or holding
 * something unparseable becomes NaN, which serialises to null and is refused server-side in
 * the server's own words — which is the point: one bound, one enforcer, one sentence.
 */
function RateLimitsEditor({ saved }: { saved: OperationalSettingsPublic }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<RateLimitDraft>(() => rateLimitDraftFrom(saved));
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: updateOperationalSettings,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.operationalSettings });
      setError(null);
      showToast(STR.toast.rateLimitsSaved);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const dirty =
    draft.loginMax !== String(saved.loginRateLimitMax) ||
    draft.loginWindow !== String(saved.loginRateLimitWindowSeconds) ||
    draft.introspectMax !== String(saved.introspectRateLimitMax) ||
    draft.introspectWindow !== String(saved.introspectRateLimitWindowSeconds) ||
    draft.introspectFailureMax !== String(saved.introspectFailureRateLimitMax) ||
    draft.introspectFailureWindow !== String(saved.introspectFailureRateLimitWindowSeconds);

  function set<K extends keyof RateLimitDraft>(key: K, value: RateLimitDraft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
    setError(null);
  }

  function save() {
    saveMutation.mutate({
      // The nine this card does not own, at their saved values — see the note above.
      healthPollIntervalSeconds: saved.healthPollIntervalSeconds,
      healthPollFailureThreshold: saved.healthPollFailureThreshold,
      sessionTtlDays: saved.sessionTtlDays,
      introspectionCacheTtlSeconds: saved.introspectionCacheTtlSeconds,
      auditPageSize: saved.auditPageSize,
      sessionPurgeDays: saved.sessionPurgeDays,
      totpDriftSteps: saved.totpDriftSteps,
      totpRequirement: saved.totpRequirement,
      announcementClampLines: saved.announcementClampLines,
      loginRateLimitMax: Number(draft.loginMax),
      loginRateLimitWindowSeconds: Number(draft.loginWindow),
      introspectRateLimitMax: Number(draft.introspectMax),
      introspectRateLimitWindowSeconds: Number(draft.introspectWindow),
      introspectFailureRateLimitMax: Number(draft.introspectFailureMax),
      introspectFailureRateLimitWindowSeconds: Number(draft.introspectFailureWindow),
    });
  }

  const fields: {
    key: keyof RateLimitDraft;
    label: string;
    hint: string;
    min: number;
    max: number;
  }[] = [
    {
      key: 'loginMax',
      label: STR.settings.loginRateLimitLabel,
      hint: STR.settings.loginRateLimitHint,
      min: RATE_LIMIT_COUNT_MIN,
      max: LOGIN_RATE_LIMIT_MAX_BOUND,
    },
    {
      key: 'loginWindow',
      label: STR.settings.loginRateLimitWindowLabel,
      hint: STR.settings.loginRateLimitWindowHint,
      min: RATE_LIMIT_WINDOW_MIN,
      max: RATE_LIMIT_WINDOW_MAX,
    },
    {
      key: 'introspectMax',
      label: STR.settings.introspectRateLimitLabel,
      hint: STR.settings.introspectRateLimitHint,
      min: RATE_LIMIT_COUNT_MIN,
      max: INTROSPECT_RATE_LIMIT_MAX_BOUND,
    },
    {
      key: 'introspectWindow',
      label: STR.settings.introspectRateLimitWindowLabel,
      hint: STR.settings.introspectRateLimitWindowHint,
      min: RATE_LIMIT_WINDOW_MIN,
      max: RATE_LIMIT_WINDOW_MAX,
    },
    {
      key: 'introspectFailureMax',
      label: STR.settings.introspectFailureRateLimitLabel,
      hint: STR.settings.introspectFailureRateLimitHint,
      min: RATE_LIMIT_COUNT_MIN,
      max: INTROSPECT_FAILURE_RATE_LIMIT_MAX_BOUND,
    },
    {
      key: 'introspectFailureWindow',
      label: STR.settings.introspectFailureRateLimitWindowLabel,
      hint: STR.settings.introspectFailureRateLimitWindowHint,
      min: RATE_LIMIT_WINDOW_MIN,
      max: RATE_LIMIT_WINDOW_MAX,
    },
  ];

  return (
    <Card style={{ minWidth: 0 }}>
      <CardHeader title={STR.settings.rateLimitsTitle} />
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}

        {/* OPERATOR-RULED HONESTY: per-instance, in-process counters. The sentence must not
            imply a suite-wide ceiling the implementation does not deliver. */}
        <p
          className="tx-subtle"
          style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, textWrap: 'pretty' }}
        >
          {STR.settings.rateLimitsLead}
        </p>

        {fields.map((field) => (
          <label key={field.key} style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {field.label}
            </span>
            <input
              className={error === null ? 'field' : 'field field-over'}
              type="number"
              inputMode="numeric"
              min={field.min}
              max={field.max}
              step={1}
              value={draft[field.key]}
              onChange={(event) => {
                set(field.key, event.target.value);
              }}
              style={textFieldStyle}
            />
            <span className="tx-subtle" style={hintStyle}>
              {field.hint}
            </span>
          </label>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '12px 22px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 11px 11px',
        }}
      >
        <button
          type="button"
          className="tbtn"
          style={quietButtonStyle}
          disabled={!dirty}
          onClick={() => {
            setDraft(rateLimitDraftFrom(saved));
            setError(null);
            showToast(STR.toast.rateLimitsDiscarded, 'warn');
          }}
        >
          {STR.settings.discardChanges}
        </button>
        {dirty ? (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Chip tone="attention">{STR.settings.unsaved}</Chip>
            <button
              type="button"
              className="prim"
              style={primaryButtonStyle}
              disabled={saveMutation.isPending}
              onClick={save}
            >
              {saveMutation.isPending ? STR.settings.saving : STR.settings.save}
            </button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** `.dtable th` is the small uppercase column head; a row head here reads as a label. */
const switchRowHeadStyle: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: 'var(--text)',
  whiteSpace: 'normal',
};

const SWITCH_ROLE_LABEL: Record<SwitchRole, string> = {
  hr: STR.settings.access.switchColumnHr,
  it: STR.settings.access.switchColumnIt,
};

const SWITCH_LABEL: Record<SwitchKey, string> = {
  seeMatrix: STR.settings.access.switchSeeMatrix,
  exportMatrix: STR.settings.access.switchExportMatrix,
  seeCatalog: STR.settings.access.switchSeeCatalog,
};

/**
 * SIR-7804 — the access module's settings, on Settings → Access: how old an open app
 * deactivation gets before it shows as overdue, and (SIR-7864) the six switches for what HR
 * and IT may see. The same anatomy as the Operations card (dirty-tracked, one write,
 * discard/unsaved/save footer); the bounds are client-side affordance only and the server
 * refuses independently. The switches open the menu entries here and the routes on the
 * server; the server is what enforces them.
 */
function AccessSettingsEditor({ saved }: { saved: AccessSettingsPublic }) {
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(() => String(saved.deprovisionOverdueHours));
  const [switches, setSwitches] = useState(() => saved.switches);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: updateAccessSettings,
    onSuccess: () => {
      // The Queue and the offboarding checklists compute "overdue" from this value.
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
      setError(null);
      showToast(STR.toast.accessSettingsSaved);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const dirty =
    hours !== String(saved.deprovisionOverdueHours) || !switchesEqual(switches, saved.switches);
  const exportAlone = exportWithoutSee(switches);

  function save() {
    const value = Number(hours);
    if (!Number.isInteger(value) || value < OVERDUE_HOURS_MIN || value > OVERDUE_HOURS_MAX) {
      setError(STR.settings.access.overdueRange(OVERDUE_HOURS_MIN, OVERDUE_HOURS_MAX));
      return;
    }
    saveMutation.mutate({ deprovisionOverdueHours: value, switches });
  }

  return (
    <Card>
      <CardHeader title={STR.settings.access.title} />
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}

        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.access.overdueLabel} ({STR.settings.access.hoursUnit})
          </span>
          <input
            className={error === null ? 'field' : 'field field-over'}
            type="number"
            inputMode="numeric"
            min={OVERDUE_HOURS_MIN}
            max={OVERDUE_HOURS_MAX}
            step={1}
            value={hours}
            onChange={(event) => {
              setHours(event.target.value);
              setError(null);
            }}
            style={textFieldStyle}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.access.overdueHint}
          </span>
        </label>

        <div>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.settings.access.switchesTitle}
          </span>
          <table
            className="dtable"
            aria-label={STR.settings.access.switchesCaption}
            style={{ width: 'auto', minWidth: 360 }}
          >
            <thead>
              <tr>
                <th scope="col" />
                {SWITCH_ROLES.map((role) => (
                  <th key={role} scope="col" style={{ textAlign: 'center', width: 72 }}>
                    {SWITCH_ROLE_LABEL[role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SWITCH_KEYS.map((key) => (
                <tr key={key}>
                  <th scope="row" style={switchRowHeadStyle}>
                    {SWITCH_LABEL[key]}
                    {key === 'seeCatalog' ? (
                      <span className="tx-subtle" style={{ ...hintStyle, fontWeight: 400 }}>
                        {STR.settings.access.switchSeeCatalogHint}
                      </span>
                    ) : null}
                  </th>
                  {SWITCH_ROLES.map((role) => (
                    <td key={role} style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={STR.settings.access.switchLabel(
                          SWITCH_LABEL[key],
                          SWITCH_ROLE_LABEL[role],
                        )}
                        checked={switches[role][key]}
                        onChange={(event) => {
                          setSwitches(withSwitch(switches, role, key, event.target.checked));
                          setError(null);
                        }}
                        style={{ ...checkboxStyle, margin: 0 }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <span className="tx-subtle" style={hintStyle}>
            {STR.settings.access.switchesLead}
          </span>
          {exportAlone.map((role) => (
            <Callout key={role} tone="attention" style={{ marginTop: 8 }}>
              {STR.settings.access.exportNeedsSee(SWITCH_ROLE_LABEL[role])}
            </Callout>
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '12px 22px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 11px 11px',
        }}
      >
        <button
          type="button"
          className="tbtn"
          style={quietButtonStyle}
          disabled={!dirty}
          onClick={() => {
            setHours(String(saved.deprovisionOverdueHours));
            setSwitches(saved.switches);
            setError(null);
            showToast(STR.toast.accessSettingsDiscarded, 'warn');
          }}
        >
          {STR.settings.discardChanges}
        </button>
        {dirty ? (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Chip tone="attention">{STR.settings.unsaved}</Chip>
            <button
              type="button"
              className="prim"
              style={primaryButtonStyle}
              disabled={saveMutation.isPending}
              onClick={save}
            >
              {saveMutation.isPending ? STR.settings.saving : STR.settings.save}
            </button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * THE LAUNCHER TAG MANAGER. Built to the announcement type manager's pattern exactly —
 * same table shape, same "used on" count, same Active/Retired state chip, same empty state,
 * same long-name truncation — MINUS the colour column, because TAGS ARE NEUTRAL: no colour,
 * no swatch, ever.
 *
 * The one behavioural difference from types, and it is a ruling rather than an oversight: a
 * retired tag is RESTORABLE, and stays renameable while retired. A retired announcement type
 * is neither, because its name and colour are already rendering on historical announcements.
 */
function LauncherTagsCard() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<
    { kind: 'form'; tag: ApplicationTagPublic | null } | { kind: 'retire'; tag: ApplicationTagPublic } | null
  >(null);

  const tagsQuery = useQuery({ queryKey: queryKeys.applicationTags, queryFn: listApplicationTags });
  const tags = tagsQuery.data?.data ?? [];
  const active = tags.filter((tag) => tag.retiredAt === null).length;

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.applicationTags });
    // A tag's name is joined onto every application carrying it, on both surfaces.
    void queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    void queryClient.invalidateQueries({ queryKey: queryKeys.myApplications });
  }

  const retireMutation = useMutation({
    mutationFn: (tag: ApplicationTagPublic) => retireApplicationTag(tag.id),
    onSuccess: (_result, tag) => {
      invalidate();
      setModal(null);
      showToast(STR.toast.tagRetired(tag.name), 'warn');
    },
    onError: (failure) => {
      setModal(null);
      showToast(failure instanceof ApiError ? failure.message : STR.errors.generic, 'warn');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (tag: ApplicationTagPublic) => restoreApplicationTag(tag.id),
    onSuccess: (_result, tag) => {
      invalidate();
      showToast(STR.toast.tagRestored(tag.name));
    },
    onError: (failure) => {
      showToast(failure instanceof ApiError ? failure.message : STR.errors.generic, 'warn');
    },
  });

  return (
    <>
      <Card>
        <CardHeader
          title={STR.settings.tags.title}
          action={
            <button
              type="button"
              className="tbtn"
              style={quietButtonStyle}
              onClick={() => {
                setModal({ kind: 'form', tag: null });
              }}
            >
              {STR.settings.tags.newTag}
            </button>
          }
        />
        <div style={{ padding: '18px 22px 0' }}>
          <p
            className="tx-subtle"
            style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, textWrap: 'pretty' }}
          >
            {STR.settings.tags.lead}
          </p>
        </div>

        {tagsQuery.isLoading ? (
          <div style={{ padding: '18px 22px' }} aria-hidden="true">
            <Skeleton style={{ height: 11, width: 200, borderRadius: 5 }} />
            <Skeleton style={{ height: 11, width: 160, borderRadius: 5, marginTop: 10 }} />
          </div>
        ) : null}

        {!tagsQuery.isLoading && tagsQuery.isError ? (
          <div style={{ padding: '18px 22px' }}>
            <Callout
              tone="danger"
              role="alert"
              style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
              <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
              <button
                type="button"
                className="tbtn"
                style={quietButtonStyle}
                onClick={() => {
                  void tagsQuery.refetch();
                }}
              >
                {STR.common.tryAgain}
              </button>
            </Callout>
          </div>
        ) : null}

        {!tagsQuery.isLoading && !tagsQuery.isError && tags.length === 0 ? (
          <EmptyRow>
            <div style={{ fontWeight: 650 }}>{STR.settings.tags.emptyTitle}</div>
            <p
              className="tx-subtle"
              style={{ maxWidth: 420, margin: '8px auto 0', fontSize: 12.5, lineHeight: 1.6 }}
            >
              {STR.settings.tags.emptyBody}
            </p>
          </EmptyRow>
        ) : null}

        {!tagsQuery.isLoading && !tagsQuery.isError && tags.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <div
              className="tx-subtle"
              style={{
                ...eyebrowStyle,
                padding: '12px 18px',
                borderTop: '1px solid var(--border)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
              }}
            >
              {STR.settings.tags.meta(active, tags.length - active)}
            </div>
            <div className="dscroll">
              <table className="dtable">
                <thead>
                  <tr>
                    <th scope="col">{STR.settings.tags.columns.tag}</th>
                    <th scope="col">{STR.settings.tags.columns.usedOn}</th>
                    <th scope="col">{STR.settings.tags.columns.state}</th>
                    <th scope="col" className="right">
                      {STR.settings.tags.columns.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => {
                    const retired = tag.retiredAt !== null;
                    return (
                      <tr key={tag.id} className="row" {...(retired ? { 'data-off': true } : {})}>
                        <td style={{ maxWidth: 280 }}>
                          {/* NEUTRAL — the tag manager has no colour column and no swatch,
                              and the pill is the launcher's own neutral one. */}
                          <span className="tag-pill">
                            <span className="ptr">{tag.name}</span>
                          </span>
                        </td>
                        <td
                          className="tx-muted"
                          style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                        >
                          {tag.usedOnCount}
                        </td>
                        <td>
                          <Chip tone={retired ? 'neutral' : 'positive'} size="sm">
                            {retired ? STR.settings.tags.stateRetired : STR.settings.tags.stateActive}
                          </Chip>
                        </td>
                        <td className="right">
                          <button
                            type="button"
                            className="tbtn"
                            style={rowActionStyle}
                            onClick={() => {
                              setModal({ kind: 'form', tag });
                            }}
                          >
                            {STR.settings.tags.edit}
                          </button>
                          {retired ? (
                            <button
                              type="button"
                              className="tbtn"
                              style={rowActionStyle}
                              disabled={restoreMutation.isPending}
                              onClick={() => {
                                restoreMutation.mutate(tag);
                              }}
                            >
                              {STR.settings.tags.restore}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="tbtn tbtn-danger"
                              style={rowActionStyle}
                              onClick={() => {
                                setModal({ kind: 'retire', tag });
                              }}
                            >
                              {STR.settings.tags.retire}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Card>

      {modal?.kind === 'form' ? (
        <TagFormModal
          tag={modal.tag}
          onDone={invalidate}
          onClose={() => {
            setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'retire' ? (
        <Modal
          title={STR.modals.retireApplicationTag.title(modal.tag.name)}
          onClose={() => {
            setModal(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setModal(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={retireMutation.isPending}
                onClick={() => {
                  retireMutation.mutate(modal.tag);
                }}
              >
                {STR.modals.retireApplicationTag.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>
              {STR.modals.retireApplicationTag.body(modal.tag.usedOnCount)}
            </ModalParagraph>
            <p className="tx-subtle" style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.6 }}>
              {STR.modals.retireApplicationTag.restorable}
            </p>
          </ModalProse>
        </Modal>
      ) : null}
    </>
  );
}

/** New / edit launcher tag. Name only — there is deliberately no colour control here. */
function TagFormModal({
  tag,
  onDone,
  onClose,
}: {
  tag: ApplicationTagPublic | null;
  onDone: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(tag?.name ?? '');
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => {
      const trimmed = name.trim();
      return tag === null
        ? createApplicationTag({ name: trimmed })
        : updateApplicationTag(tag.id, { name: trimmed });
    },
    onSuccess: (result) => {
      onDone();
      showToast(tag === null ? STR.toast.tagCreated(result.tag.name) : STR.toast.tagUpdated);
      onClose();
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  function submit() {
    if (name.trim() === '') {
      setError(STR.modals.applicationTag.nameRequired);
      return;
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      title={tag === null ? STR.modals.applicationTag.titleNew : STR.modals.applicationTag.titleEdit}
      lead={STR.modals.applicationTag.lead}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={saveMutation.isPending}
            onClick={submit}
          >
            {tag === null
              ? STR.modals.applicationTag.submitNew
              : STR.modals.applicationTag.submitEdit}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.applicationTag.nameLabel}
          </span>
          <input
            className="field"
            type="text"
            maxLength={60}
            value={name}
            placeholder={STR.modals.applicationTag.namePlaceholder}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            style={textFieldStyle}
          />
        </label>
      </ModalForm>
    </Modal>
  );
}
